import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@cwos/api-client';
import { ConsequenceNote, Pill, SkeletonSet } from '@cwos/ui-admin';
import { SettingsCard, SettingsHeader } from './common.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * How you are notified — your own preferences, not the organisation's.
 *
 * Two distinctions the screen has to make plainly, because getting either wrong
 * would make it a lie:
 *
 *   PERSONAL, NOT ORGANISATIONAL. Turning off mileage push changes how YOU are
 *   reached. It does not touch the organisation's automation rules, and an
 *   administrator cannot use this screen to change anyone else's choices —
 *   there is no route that would accept it.
 *
 *   A CHANNEL, NOT THE RECORD. Switching push off stops your phone buzzing. The
 *   notification is still in your Notification Centre when you look. Anything
 *   else would be a preference that silently loses information.
 *
 * Email and SMS are absent on purpose. Neither has a per-user delivery path in
 * Phibian, and a toggle that does nothing is worse than no toggle at all.
 */
export function NotificationPreferences() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: () => notifications.preferences(),
  });

  const [draft, setDraft] = useState<notifications.NotificationPreference[] | null>(null);
  const [quiet, setQuiet] = useState<notifications.QuietHours | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data && draft === null) {
      setDraft(query.data.preferences);
      setQuiet(query.data.quietHours);
    }
  }, [query.data, draft]);

  const save = useMutation({
    mutationFn: (rows: notifications.NotificationPreference[]) =>
      notifications.updatePreferences({
        preferences: rows
          .filter((p) => !p.mandatory)
          .map((p) => ({ category: p.category, pushEnabled: p.pushEnabled })),
        quietHours: quiet ?? undefined,
      }),
    onSuccess: (result) => {
      setDraft(result.preferences);
      setQuiet(result.quietHours);
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['notifications', 'preferences'] });
    },
  });

  const suggestions = useQuery({
    queryKey: ['notifications', 'preference-suggestions'],
    queryFn: () => notifications.preferenceSuggestions(),
  });
  const refreshSuggestions = () =>
    void qc.invalidateQueries({ queryKey: ['notifications', 'preference-suggestions'] });
  // Apply is the person's own preference save — the same toggle undoes it.
  const apply = useMutation({
    mutationFn: (s: notifications.NotificationSuggestion) =>
      notifications.updatePreferences(
        s.action === 'push_off' && s.category
          ? { preferences: [{ category: s.category, pushEnabled: false }] }
          : {
              preferences: (query.data?.preferences ?? [])
                .filter((p) => !p.mandatory)
                .map((p) => ({ category: p.category, pushEnabled: p.pushEnabled })),
              quietHours: { enabled: true, ...s.quietHours! },
            },
      ),
    onSuccess: (result) => {
      setDraft(result.preferences);
      setQuiet(result.quietHours);
      void qc.invalidateQueries({ queryKey: ['notifications', 'preferences'] });
      refreshSuggestions();
    },
  });
  const dismiss = useMutation({
    mutationFn: (key: string) => notifications.dismissPreferenceSuggestion(key),
    onSuccess: refreshSuggestions,
  });

  if (query.isLoading) return <SkeletonSet surface="table" rows={5} />;
  if (query.isError && !query.data) {
    return (
      <LoadFailure
        error={query.error}
        message="We couldn’t load your notification settings just now."
        onRetry={() => void query.refetch()}
      />
    );
  }

  const rows = draft ?? query.data!.preferences;
  const set = (category: string, pushEnabled: boolean) => {
    setSaved(false);
    setDraft(rows.map((p) => (p.category === category ? { ...p, pushEnabled } : p)));
  };

  return (
    <div className="rn-col" style={{ maxWidth: 680 }}>
      <SettingsHeader
        title="Notifications"
        blurb="How Phibian reaches you. These are your own settings — they do not change anything for your colleagues."
      />

      {(suggestions.data?.suggestions ?? []).map((s) => (
        <SettingsCard key={s.key}>
          <h2 className="rn-card-title">Phibian noticed</h2>
          <p style={{ marginTop: 'var(--space-6)' }}>{s.message}</p>
          <p className="rn-muted" style={{ marginTop: 'var(--space-4)' }}>
            Based on your last 14 days:{' '}
            {Object.entries(s.evidence)
              .map(([k, v]) => `${k.replace(/([A-Z])/g, ' $1').toLowerCase()} ${v}`)
              .join(' · ')}
            . You can change this back below at any time.
          </p>
          <div style={{ marginTop: 'var(--space-12)', display: 'flex', gap: 'var(--space-12)' }}>
            <button
              type="button"
              className="signin-devbtn"
              style={{ width: 'auto', padding: 'var(--space-8) var(--space-14)' }}
              disabled={apply.isPending || dismiss.isPending}
              onClick={() => apply.mutate(s)}
            >
              Apply
            </button>
            <button
              type="button"
              className="rn-btn-quiet"
              disabled={apply.isPending || dismiss.isPending}
              onClick={() => dismiss.mutate(s.key)}
            >
              Not now
            </button>
          </div>
        </SettingsCard>
      ))}

      <SettingsCard>
        <h2 className="rn-card-title">Push to your phone</h2>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          Everything still appears in your Notification Centre. Switching one off only stops the
          phone buzzing.
        </p>

        <div style={{ marginTop: 'var(--space-16)', display: 'grid', gap: 'var(--space-14)' }}>
          {rows.map((pref) => (
            <div key={pref.category}>
              <label
                style={{
                  display: 'flex',
                  gap: 'var(--space-8)',
                  alignItems: 'center',
                }}
              >
                <input
                  type="checkbox"
                  checked={pref.pushEnabled}
                  disabled={pref.mandatory}
                  aria-describedby={`pref-${pref.category}-desc`}
                  onChange={(e) => set(pref.category, e.target.checked)}
                />
                <span style={{ fontWeight: 600 }}>{pref.label}</span>
                {pref.mandatory ? (
                  <Pill variant="plain" dot>
                    Always on
                  </Pill>
                ) : null}
              </label>
              <p
                id={`pref-${pref.category}-desc`}
                className="rn-muted"
                style={{ marginTop: 'var(--space-4)', marginLeft: 'var(--space-20)' }}
              >
                {pref.description}
                {pref.mandatory
                  ? ' Security notices cannot be switched off — being unable to hear that your access changed is a risk, not a preference.'
                  : ''}
              </p>
            </div>
          ))}
        </div>

        <ConsequenceNote text="These settings are yours alone. Your organisation's automation rules decide what gets sent; this decides how it reaches you." />

        <div
          style={{
            marginTop: 'var(--space-16)',
            display: 'grid',
            gap: 'var(--space-8)',
            alignItems: 'center',
          }}
        >
          <label
            style={{
              display: 'flex',
              gap: 'var(--space-8)',
              alignItems: 'center',
            }}
          >
            <input
              type="checkbox"
              checked={quiet?.enabled ?? false}
              onChange={(e) => {
                setSaved(false);
                setQuiet((q) => ({
                  ...(q ?? { enabled: true, start: '21:00', end: '08:00' }),
                  enabled: e.target.checked,
                }));
              }}
            />
            <span style={{ fontWeight: 600 }}>Quiet hours</span>
          </label>
          <p className="rn-muted" style={{ marginTop: 'var(--space-4)' }}>
            Between these times your phone stays silent — in your organisation's timezone. An end
            time before the start means it runs overnight. Security notices always come through.
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-12)', alignItems: 'center' }}>
            <label style={{ display: 'flex', gap: 'var(--space-6)', alignItems: 'center' }}>
              <span>From</span>
              <input
                type="time"
                value={quiet?.start ?? '21:00'}
                disabled={!quiet?.enabled}
                onChange={(e) => {
                  setSaved(false);
                  setQuiet((q) => ({
                    ...(q ?? { enabled: true, start: '21:00', end: '08:00' }),
                    start: e.target.value,
                  }));
                }}
              />
            </label>
            <label style={{ display: 'flex', gap: 'var(--space-6)', alignItems: 'center' }}>
              <span>to</span>
              <input
                type="time"
                value={quiet?.end ?? '08:00'}
                disabled={!quiet?.enabled}
                onChange={(e) => {
                  setSaved(false);
                  setQuiet((q) => ({
                    ...(q ?? { enabled: true, start: '21:00', end: '08:00' }),
                    end: e.target.value,
                  }));
                }}
              />
            </label>
          </div>
        </div>

        <div
          style={{
            marginTop: 'var(--space-16)',
            display: 'flex',
            gap: 'var(--space-12)',
            alignItems: 'center',
          }}
        >
          <button
            type="button"
            className="signin-devbtn"
            style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
            disabled={save.isPending || (quiet?.enabled === true && quiet.start === quiet.end)}
            onClick={() => save.mutate(rows)}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          {saved ? <span className="rn-muted">Saved.</span> : null}
        </div>
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Other ways to reach you</h2>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          Email and text message are not yet delivery options for personal notifications, so there
          is nothing to switch on here. Your organisation may separately use SMS for operational
          alerts — that is configured under Notifications &amp; SMS.
        </p>
      </SettingsCard>
    </div>
  );
}
